import { Building2, LogOut, UserCog } from "lucide-react";
import { useState } from "react";

import { ConfirmDelete } from "@/components/shared/ConfirmDelete";
import { useStopImpersonation } from "@/features/auth/hooks";
import { useAuthStore } from "@/features/auth/store";

/**
 * Bandeau affiché en permanence lorsqu'un super-administrateur a usurpé la
 * session d'un autre utilisateur. Rappelle QUI agit réellement (l'usurpateur),
 * QUEL compte est usurpé, et SUR QUELLE clinique porte la session ; le retour à
 * son propre compte est confirmé pour éviter une sortie accidentelle.
 */
export function ImpersonationBanner() {
  const me = useAuthStore((s) => s.me);
  const stopMut = useStopImpersonation();
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (!me?.is_impersonating) return null;

  const impersonated = me.full_name || me.email;
  const actor = me.impersonator?.full_name || me.impersonator?.email;
  const activeOrg = me.memberships.find(
    (m) => m.organisation_id === me.active_organisation_id,
  )?.organisation_name;

  return (
    <>
      <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-warning px-4 py-2 text-center text-sm font-medium text-white">
        <span className="inline-flex items-center gap-2">
          <UserCog className="h-4 w-4" aria-hidden />
          Session usurpée : <strong>{impersonated}</strong>
          {actor && (
            <span className="font-normal opacity-90">— vous êtes connecté en tant que {actor}</span>
          )}
        </span>
        {activeOrg && (
          <span className="inline-flex items-center gap-1.5 opacity-90">
            <Building2 className="h-3.5 w-3.5" aria-hidden />
            {activeOrg}
          </span>
        )}
        <button
          type="button"
          onClick={() => setConfirmOpen(true)}
          disabled={stopMut.isPending}
          className="inline-flex items-center gap-1.5 bg-black/15 px-2.5 py-1 font-semibold underline-offset-2 hover:bg-black/25 disabled:opacity-60"
        >
          <LogOut className="h-3.5 w-3.5" aria-hidden />
          Revenir à mon compte
        </button>
      </div>

      <ConfirmDelete
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Quitter la session usurpée ?"
        description={`Vous quittez la session de ${impersonated}.`}
        confirmText={`Vous reviendrez à votre compte${actor ? ` (${actor})` : ""}.`}
        confirmLabel="Revenir à mon compte"
        isPending={stopMut.isPending}
        onConfirm={() => stopMut.mutate(undefined, { onSuccess: () => setConfirmOpen(false) })}
      />
    </>
  );
}
